import type { DataOption } from '@/common.types'
import type { FieldSchemaModel } from '@/shared/generated'
import type { TreeNodeType } from '@/shared/lib/tree'

import { useMemo } from 'react'
import { filter, map, paths, pick, pipe, prop } from 'lodash/fp'
import { useTranslation } from 'react-i18next'

import {
	isAnchorStageField,
	toAdaptedSchema,
	useColumnsSchemaQuery,
} from '@/entities/column'
import {
	toCategoriesTree,
	useBrandsQuery,
	useCategoriesQuery,
} from '@/entities/core'
import {
	getSalesEntitiesTree,
	useOptimizationGroupsQuery,
	useSalesEntitiesQuery,
} from '@/entities/optimization-group'

import { getFiledConfig } from '@/shared/components/query-builder'
import { HiddenOnContext } from '@/shared/generated'
import { toOption } from '@/shared/tools/utils'

export const useStrategyFieldsConfig = () => {
	const { t } = useTranslation()

	const { data: brands, isLoading: isBrandsLoading } = useBrandsQuery(
		{},
		{ select: pipe([map(paths(['id', 'name'])), toOption]) }
	)
	const { data: categories, isLoading: isCategoriesLoading } =
		useCategoriesQuery<TreeNodeType[]>({}, { select: toCategoriesTree })
	const { data: optimizationGroups, isLoading: isOGsLoading } =
		useOptimizationGroupsQuery({
			select: pipe([map(paths(['id', 'name'])), toOption]),
		})
	const { data: posIds, isLoading: isPosIdsLoading } = useSalesEntitiesQuery({
		select: getSalesEntitiesTree,
	})
	const fieldsQuery = useColumnsSchemaQuery<FieldSchemaModel[]>(
		HiddenOnContext.Assortment,
		{ select: (data) => data.filter(isAnchorStageField) }
	)
	const aggregationFieldsQuery = useColumnsSchemaQuery<FieldSchemaModel[]>(
		HiddenOnContext.AnchorGrouping,
		{ select: (data) => data.filter(isAnchorStageField) }
	)
	const clusterizationFieldsQuery = useColumnsSchemaQuery<FieldSchemaModel[]>(
		HiddenOnContext.AnchorClustering,
		{ select: (data) => data.filter(isAnchorStageField) }
	)

	const filterFields = useMemo(() => {
		if (!fieldsQuery.data?.length) return []

		return pipe(
			filter(prop('params.filterable')),
			map((schema: FieldSchemaModel) => toAdaptedSchema(schema, t))
		)(fieldsQuery.data)
	}, [fieldsQuery.data, t])

	const fieldConfig = useMemo(
		() =>
			getFiledConfig(filterFields, {
				brands: brands || [],
				category_ids: categories || [],
				optimization_group_id: optimizationGroups || [],
				pos_ids: posIds || [],
			}),
		[filterFields, brands, categories, optimizationGroups, posIds]
	)

	const aggregationColumnOptions = useMemo(
		() =>
			pipe(
				map((schema: FieldSchemaModel) => toAdaptedSchema(schema, t)),
				map(pick(['value', 'label', 'valueType']))
			)(aggregationFieldsQuery.data || []) as DataOption[],
		[aggregationFieldsQuery.data, t]
	)

	const sortableColumnOptions = useMemo(
		() =>
			pipe(
				filter(prop('params.sortable')),
				map((schema: FieldSchemaModel) => toAdaptedSchema(schema, t)),
				map(pick(['value', 'label', 'valueType']))
			)(fieldsQuery.data || []) as DataOption[],
		[fieldsQuery.data, t]
	)

	const clusterizationColumnOptions = useMemo(
		() =>
			pipe(
				map((schema: FieldSchemaModel) => toAdaptedSchema(schema, t)),
				map(pick(['value', 'label', 'valueType']))
			)(clusterizationFieldsQuery.data || []) as DataOption[],
		[clusterizationFieldsQuery.data, t]
	)

	return {
		isLoading:
			fieldsQuery.isLoading ||
			aggregationFieldsQuery.isLoading ||
			clusterizationFieldsQuery.isLoading ||
			isBrandsLoading ||
			isCategoriesLoading ||
			isOGsLoading ||
			isPosIdsLoading,
		fieldConfig,
		aggregationColumnOptions,
		sortableColumnOptions,
		clusterizationColumnOptions,
	}
}
